import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { Review } from '../components/ReviewItem/ReviewItem';
import { ReviewFromDB } from './useReviews';

export interface ReviewDetail extends Review {
  description: string;
  created_at: string;
  last_updated_at?: string;
  project_id: string;
  owner_user_id: string;
  status?: string;
  languages_done?: string[];
}

/**
 * Custom hook for managing review details
 * @param reviewId - The ID of the review to fetch
 * @returns Object containing review details, loading state, and error state
 */
export function useReviewDetail(reviewId: string | undefined) {
  const { user } = useAuth();
  const [review, setReview] = useState<ReviewDetail | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch review details when the review ID changes
  useEffect(() => {
    const fetchReviewDetail = async () => {
      if (!user || !reviewId) {
        return;
      }
      
      try {
        setIsLoading(true);
        setError(null);
        
        const { data, error } = await supabase
          .from('Reviews')
          .select('*')
          .eq('id', reviewId)
          .single();
          
        if (error) {
          setError('Failed to load review details. Please try again.');
          return;
        }
        
        if (data) {
          const reviewDetail: ReviewDetail = {
            id: data.id,
            title: data.title,
            description: data.description || '',
            keys: data.keys || 0,
            assignee: data.assignee || 'Unassigned',
            completed: data.status === 'completed', // Convert status to completed boolean for UI
            created_at: data.created_at,
            last_updated_at: data.last_updated_at,
            project_id: data.project_id,
            owner_user_id: data.owner_user_id,
            status: data.status,
            languages_done: data.languages_done || []
          };
          
          setReview(reviewDetail);
        }
      } catch (error) {
        setError('An unexpected error occurred. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchReviewDetail();
  }, [user, reviewId]);

  /**
   * Update review details
   * @param updates - Object containing the fields to update
   * @returns A boolean indicating whether the operation was successful
   */
  const updateReview = async (updates: Partial<ReviewDetail>) => {
    if (!user || !reviewId || !review) {
      return false;
    }
    
    try {
      setIsLoading(true);
      
      // Convert completed boolean to status string if it's being updated
      const dbUpdates: any = { ...updates };
      if ('completed' in updates) {
        dbUpdates.status = updates.completed ? 'completed' : 'pending';
        delete dbUpdates.completed;
      }
      
      const { error } = await supabase
        .from('Reviews')
        .update({
          ...dbUpdates,
          last_updated_at: new Date().toISOString()
        })
        .eq('id', reviewId);
        
      if (error) {
        setError('Failed to update review. Please try again.');
        return false;
      }
      
      // Update local state
      setReview(prevReview => {
        if (!prevReview) return null;
        
        const updatedReview = { 
          ...prevReview, 
          ...updates, 
          last_updated_at: new Date().toISOString() 
        };
        
        // If status was updated in the database, update the completed flag in the UI
        if (dbUpdates.status) {
          updatedReview.status = dbUpdates.status;
          updatedReview.completed = dbUpdates.status === 'completed';
        }
        
        return updatedReview;
      });
      
      return true;
    } catch (error) {
      setError('An unexpected error occurred. Please try again.');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Toggle the completion status of the review
   * @returns A boolean indicating whether the operation was successful
   */
  const toggleReviewCompletion = async () => {
    if (!review) {
      return false;
    }
    
    return updateReview({ completed: !review.completed });
  };

  return {
    review,
    isLoading,
    error,
    updateReview,
    toggleReviewCompletion,
    setReview: (updater: ((prev: ReviewDetail | null) => ReviewDetail | null) | ReviewDetail | null) => setReview(updater)
  };
} 